"use client";

import React from "react";
import { Package, ImagePlus, Loader, CircleCheck, CircleAlert } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
const CATEGORIES = [{
  id: "wedding",
  name: "Wedding Celebration"
}, {
  id: "bridal",
  name: "Bridal Shower"
}, {
  id: "corporate",
  name: "Corporate Event"
}, {
  id: "private",
  name: "Private Party"
}];
function ProductFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <Alert className="bg-primary/10 border-primary/20 dark:bg-primary/5 dark:border-primary/20">
        <CircleCheck className="size-5 text-primary dark:text-primary" />
        <AlertTitle className="text-primary dark:text-primary font-default font-semibold">
          Inquiry Received
        </AlertTitle>
        <AlertDescription className="text-foreground dark:text-cream mt-2">
          {successMessage}
          <Button onClick={resetForm} variant="outline" className="mt-4 w-full sm:w-auto border-secondary text-secondary hover:bg-secondary/10 dark:border-secondary dark:text-secondary dark:hover:bg-secondary/10">
            Send Another Inquiry
          </Button>
        </AlertDescription>
      </Alert>;
  }
  const inputClasses = "bg-card border-0 border-b border-secondary rounded-md focus-visible:ring-0 focus-visible:border-primary focus-visible:shadow-[0_4px_12px_-2px_rgba(244,197,207,0.3)] transition-all";
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-6">
        {submitError && <Alert variant="destructive">
            <CircleAlert className="size-5" />
            <AlertTitle className="font-default font-semibold">Error</AlertTitle>
            <AlertDescription>{submitError}</AlertDescription>
          </Alert>}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {}
          <FormField control={control} name="name" rules={{
          required: "Your name is required",
          minLength: {
            value: 3,
            message: "Name must be at least 3 characters"
          }
        }} render={({
          field
        }) => <FormItem className="md:col-span-2">
                <FormLabel className="text-primary font-default">Full Name</FormLabel>
                <FormControl>
                  <Input placeholder="e.g. Sarah Johnson" className={inputClasses} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          {}
          <FormField control={control} name="email" rules={{
          required: "Email is required",
          pattern: {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            message: "Invalid email address"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-primary font-default">Email Address</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="sarah@example.com" className={inputClasses} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          {}
          <FormField control={control} name="phone_number" rules={{
          required: "Phone number is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-primary font-default">Phone Number</FormLabel>
                <FormControl>
                  <Input placeholder="(XXX) XXX-XXXX" className={inputClasses} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          {}
          <FormField control={control} name="event_date" rules={{
          required: "Event date is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-primary font-default">Event Date</FormLabel>
                <FormControl>
                  <Input type="date" className={inputClasses} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          {}
          <FormField control={control} name="event_location" rules={{
          required: "Event location is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-primary font-default">Event Location</FormLabel>
                <FormControl>
                  <Input placeholder="City, State or Venue Name" className={inputClasses} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          {}
          <FormField control={control} name="category" rules={{
          required: "Please select an event type"
        }} render={({
          field
        }) => <FormItem className="md:col-span-2">
                <FormLabel className="text-primary font-default">Event Type</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger className="bg-card border-0 border-b border-secondary rounded-md focus:ring-0 focus:border-primary focus:shadow-[0_4px_12px_-2px_rgba(244,197,207,0.3)] transition-all w-full">
                      <SelectValue placeholder="Select event type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="bg-card border-secondary">
                    {CATEGORIES.map((cat, i) => <SelectItem key={cat.id} value={cat.id} data-index={i}>
                        {cat.name}
                      </SelectItem>)}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>} />

          {}
          <FormField control={control} name="estimated_guest_count" rules={{
          required: "Guest count is required",
          min: {
            value: 1,
            message: "Must be at least 1 guest"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-primary font-default">Estimated Guest Count</FormLabel>
                <FormControl>
                  <Input type="number" min={1} placeholder="0" className={inputClasses} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          {}
          <FormField control={control} name="description" rules={{
          required: "Please tell us about your vision",
          minLength: {
            value: 10,
            message: "Please provide a bit more detail"
          }
        }} render={({
          field
        }) => <FormItem className="md:col-span-2">
                <FormLabel className="text-primary font-default">Event Vision & Details</FormLabel>
                <FormControl>
                  <Textarea placeholder="Tell us about your dream flower bar experience..." className="min-h-[120px] resize-y bg-card border-0 border-b border-secondary rounded-md focus-visible:ring-0 focus-visible:border-primary focus-visible:shadow-[0_4px_12px_-2px_rgba(244,197,207,0.3)] transition-all" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          {}
          <FormField control={control} name="inspiration" render={({
          field: {
            value,
            onChange,
            ...fieldProps
          }
        }) => <FormItem className="md:col-span-2">
                <FormLabel className="text-primary font-default">Inspiration Photos</FormLabel>
                <FormControl>
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <Input type="file" accept="image/*" className="cursor-pointer file:cursor-pointer bg-card border-0 border-b border-secondary rounded-md focus-visible:ring-0 focus-visible:border-primary transition-all" onChange={event => {
                  onChange(event.target.files);
                }} {...fieldProps} />
                    </div>
                    <div className="hidden sm:flex size-10 shrink-0 items-center justify-center rounded-md border border-secondary/30 bg-card">
                      <ImagePlus className="size-5 text-secondary" />
                    </div>
                  </div>
                </FormControl>
                <FormDescription className="text-muted-foreground/70">
                  Upload mood boards or floral inspiration (JPG, PNG). Max 5MB.
                </FormDescription>
                <FormMessage />
              </FormItem>} />
        </div>
      </div>

      <div className="mt-8 flex justify-center">
        <Button type="submit" disabled={isSubmitting} size="lg" className="w-full sm:w-full bg-primary text-white hover:bg-primary/90 rounded-lg font-default tracking-wide shadow-md transition-all duration-300">
          {isSubmitting ? <>
              <Loader className="mr-2 size-5 animate-spin" />
              Sending Inquiry...
            </> : <>
              Send My Inquiry
            </>}
        </Button>
      </div>
    </Form>;
}
export default function PlanYourDreamEvent() {
  return <section data-section-id="257541" id="plan-your-dream-event" className="py-24 md:py-32 bg-background text-foreground">
      <div className="container mx-auto px-4 max-w-3xl">
        <div className="mb-12 text-center">
          <h2 className="text-4xl md:text-5xl font-default font-bold text-primary mb-6 tracking-wide">
            Your Luxury Flower Bar Inquiry
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto font-default leading-relaxed">
            Let's create something beautiful together. Share your event details below, and we'll help bring your luxury flower bar vision to life.
          </p>
        </div>

        <Card className="shadow-xl border-secondary/20 bg-card/50 backdrop-blur-sm rounded-xl overflow-hidden">
          <CardHeader className="bg-muted/10 border-b border-secondary/20 pb-8">
            <CardTitle className="font-semibold font-default text-2xl flex items-center gap-3 text-primary">
              <Package className="size-6" />
              Inquiry Details
            </CardTitle>
            <CardDescription className="text-muted-foreground">
              Complete the form below to receive a custom proposal for your celebration.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-8">
            <FormProvider formKey="plan_event_form_contact" formId="plan_event_form_contact" sectionName="plan-your-dream-event" defaultValues={{
            name: "",
            email: "",
            phone_number: "",
            event_date: "",
            event_location: "",
            category: "",
            estimated_guest_count: 0,
            description: "",
            inspiration: undefined
          }} submitText="Send My Inquiry" successMessage="Your inquiry has been sent beautifully. We will be in touch shortly to discuss your blooms.">
              <ProductFormInner />
            </FormProvider>
          </CardContent>
        </Card>
      </div>
    </section>;
}